<template>
  <article class="invoice-create-card invoice-create-concepts-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>{{ text.create.ui.conceptsCard.title }}</h3>
      <div v-if="!readOnly" class="invoice-create-concepts-actions">
        <button type="button" class="app-header-btn app-header-btn--primary" @click="$emit('add-concept')">
          <i class="fa fa-plus" aria-hidden="true"></i>
          <span>{{ text.create.ui.conceptsCard.addButton }}</span>
        </button>
        <div class="app-table-actions-menu-wrap">
          <button
            type="button"
            class="invoice-create-icon-btn"
            :title="text.create.ui.conceptsCard.menu.title"
            @click="showMoreActions = !showMoreActions"
          >
            <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
          </button>

          <div v-if="showMoreActions" class="app-table-actions-menu invoice-create-concepts-menu">
            <button type="button" @click="handleMoreAction('cfdi')">{{ text.create.ui.conceptsCard.menu.cfdiRelated }}</button>
            <button type="button" @click="handleMoreAction('addendas')">{{ text.create.ui.conceptsCard.menu.addendas }}</button>
            <button type="button" @click="handleMoreAction('discount')">{{ text.create.ui.conceptsCard.menu.discount }}</button>
          </div>
        </div>
      </div>
    </header>

    <div class="app-table-wrap invoice-create-concepts-table-wrap">
      <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th>{{ text.create.ui.conceptsCard.table.headers.qty }}</th>
            <th>{{ text.create.ui.conceptsCard.table.headers.unit }}</th>
            <th>{{ text.create.ui.conceptsCard.table.headers.code }}</th>
            <th>{{ text.create.ui.conceptsCard.table.headers.concept }}</th>
            <th class="app-text-right">{{ text.create.ui.conceptsCard.table.headers.unitValue }}</th>
            <th>{{ text.create.ui.conceptsCard.table.headers.taxes }}</th>
            <th class="app-text-right">{{ text.create.ui.conceptsCard.table.headers.amount }}</th>
            <th v-if="!readOnly" class="app-text-right">{{ text.create.ui.conceptsCard.table.headers.actions }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!Array.isArray(concepts) || concepts.length === 0">
            <td :colspan="readOnly ? 7 : 8" class="app-table-empty">{{ text.create.ui.conceptsCard.table.empty }}</td>
          </tr>

          <tr v-for="(concept, index) in concepts" :key="`concept-${index}`">
            <td>{{ concept.qty }}</td>
            <td>{{ concept.unit }}</td>
            <td>{{ concept.code }}</td>
            <td>
              <router-link
                v-if="buildConceptDescriptionTo(concept)"
                :to="buildConceptDescriptionTo(concept)"
                :class="['invoice-create-concept-description-link', conceptLinkClass]"
                :title="conceptLinkTitle"
              >
                {{ concept.description }}
              </router-link>
              <span v-else>{{ concept.description }}</span>
            </td>
            <td class="app-text-right">{{ formatAmountWithCurrency(concept.unitPrice, currency) }}</td>
            <td>
              <div class="expenses-cell-stack">
                <template v-for="(line, lineIndex) in resolveTaxLines(concept)" :key="`tax-line-${index}-${lineIndex}`">
                  <small class="amount-cell-tax-line">{{ formatTaxLine(line, currency) }}</small>
                </template>
              </div>
            </td>
            <td class="app-text-right"><strong>{{ formatAmountWithCurrency(concept.qty * concept.unitPrice, currency) }}</strong></td>
            <td v-if="!readOnly" class="app-text-right">
              <div class="app-table-actions-inline invoice-create-concept-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--edit"
                  :title="text.create.ui.conceptsCard.actions.editConceptTitle"
                  @click="$emit('edit-concept', { index, concept })"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete"
                  :title="text.create.ui.conceptsCard.actions.deleteConceptTitle"
                  @click="$emit('delete-concept', index)"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  </article>
</template>

<script>
import { formatTaxLine } from '@/composables/shared/useTaxPresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: invoiceText } = useDomainText('invoices');
const { catalog: generalText } = useDomainText('general');

export default {
  name: 'InvoiceCreateConceptsCard',
  props: {
    concepts: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    conceptLinkResolver: {
      type: Function,
      default: null,
    },
    conceptLinkTitle: {
      type: String,
      default: '',
    },
    conceptLinkClass: {
      type: String,
      default: '',
    },
  },
  emits: ['add-concept', 'edit-concept', 'delete-concept'],
  data() {
    return {
      text: invoiceText,
      generalText,
      showMoreActions: false,
    };
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    formatTaxLine,
    formatAmountWithCurrency,
    buildConceptDescriptionTo(concept) {
      if (typeof this.conceptLinkResolver !== 'function') {
        return null;
      }

      const target = this.conceptLinkResolver(concept);
      if (!target || typeof target !== 'object') {
        return null;
      }

      return target;
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (!target || !(target instanceof Element)) {
        return;
      }

      if (target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.showMoreActions = false;
    },
    handleMoreAction(action) {
      this.showMoreActions = false;
      if (action === 'cfdi') {
        this.$toast?.info(this.text.create.ui.conceptsCard.toasts.cfdiPending);
        return;
      }

      if (action === 'discount') {
        this.$toast?.info(this.text.create.ui.conceptsCard.toasts.discountPending);
        return;
      }

      this.$toast?.info(this.text.create.ui.conceptsCard.toasts.addendasPending);
    },
    resolveTaxAmount(concept) {
      if (Number.isFinite(Number(concept?.taxAmount))) {
        return Number(concept.taxAmount || 0);
      }

      const subtotal = (Number(concept?.qty || 0) * Number(concept?.unitPrice || 0)) || 0;
      return subtotal * 0.16;
    },
    resolveTaxLines(concept) {
      const items = Array.isArray(concept?.taxes?.items) ? concept.taxes.items : [];

      if (concept?.noTaxFallback) {
        return items;
      }

      if (items.length > 0) {
        const lines = items.map((item) => ({
          direction: String(item?.direction || '').trim().toLowerCase(),
          name: String(item?.name || item?.code || this.generalText.financial.taxes.tax).trim(),
          code: String(item?.code || '').trim(),
          rate: Number.isFinite(Number(item?.rate))
            ? Number(item.rate || 0)
            : (Number.isFinite(Number(item?.percentage)) ? Number(item.percentage || 0) : 0),
          amount: Number.isFinite(Number(item?.amount)) ? Number(item.amount || 0) : 0,
        }));

        if (lines.length > 0) {
          return lines;
        }
      }

      return [{
        direction: 'trasladado',
        name: String(concept?.taxLabel || this.generalText.financial.taxes.defaultTransferredLabel).trim() || this.generalText.financial.taxes.defaultTransferredLabel,
        amount: this.resolveTaxAmount(concept),
      }];
    },
  },
};
</script>

<style scoped>
.invoice-create-concept-description-link {
  color: inherit;
  font-weight: inherit;
  text-decoration: none;
  cursor: pointer;
}

.invoice-create-concept-description-link--detail {
  color: #0070cd;
  font-weight: 400;
  text-decoration: none;
}

.invoice-create-concept-description-link--detail:hover {
  color: #0070cd;
  text-decoration: none;
}
</style>
